import React, { useEffect, useMemo, useState } from 'react';
import AdminSidebar from './AdminSidebar';
import { getAdminDashboard } from '../../services/api';

function formatNumber(n) {
  const num = Number(n || 0);
  return num.toLocaleString();
}

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function load() {
      try {
        const token = localStorage.getItem('admin_token');
        if (!token) throw new Error('Missing admin token');

        const payload = await getAdminDashboard(token);
        if (!isMounted) return;
        setStats(payload?.stats || {});
      } catch (e) {
        if (!isMounted) return;
        setError(e?.message || 'Failed to load dashboard');
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    }

    load();

    return () => {
      isMounted = false;
    };
  }, []);

  const tileCount = stats?.total_tiles ?? 0;
  const wallpaperCount = stats?.total_wallpapers ?? 0;
  const totalContent = tileCount + wallpaperCount;

  const tilePercent = useMemo(() => {
    if (!totalContent) return 0;
    return Math.round((tileCount / totalContent) * 100);
  }, [tileCount, totalContent]);

  const wallpaperPercent = useMemo(() => {
    if (!totalContent) return 0;
    return 100 - tilePercent;
  }, [tilePercent, totalContent]);

  return (
    <div className="flex">
      <AdminSidebar />

      <main className="flex-1 ml-64 p-8">
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview</h2>

          {loading ? (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">Loading dashboard...</div>
          ) : error ? (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm text-red-600">{error}</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-gray-500">Total Retailers</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{formatNumber(stats?.total_retailers)}</p>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-gray-500">Total Products</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{formatNumber(stats?.total_products)}</p>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-gray-500">Tiles vs Wallpapers</span>
                    <span className="text-sm text-gray-500">{formatNumber(totalContent)} total</span>
                  </div>

                  <div className="space-y-3">
                    <div className="h-3 w-full bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-accent"
                        style={{ width: `${tilePercent}%` }}
                        aria-label={`Tiles: ${tilePercent}%`}
                      />
                      <div
                        className="-mt-3 h-3 bg-accent2"
                        style={{ width: `${wallpaperPercent}%`, marginLeft: `${tilePercent}%` }}
                        aria-label={`Wallpapers: ${wallpaperPercent}%`}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full bg-accent" /> Tiles
                        </span>
                      </div>
                      <div className="text-sm font-semibold text-gray-900">{tilePercent}%</div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full bg-accent2" /> Wallpapers
                        </span>
                      </div>
                      <div className="text-sm font-semibold text-gray-900">{wallpaperPercent}%</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center text-accent" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">New retailer registered</p>
                      <p className="text-sm text-gray-500">2 hours ago</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center text-accent" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Product catalog updated</p>
                      <p className="text-sm text-gray-500">1 day ago</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center text-accent" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">System maintenance completed</p>
                      <p className="text-sm text-gray-500">3 days ago</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

